import React, { Fragment } from "react";
import { concat, equals, isEmpty, map, uniqBy, prop, toUpper } from "ramda";
import makeStyles from "@mui/styles/makeStyles";
import { useRemoveContentFromCollection } from "../../collections/collectionServices";
import { useDeleteModuleItems } from "../../modules/moduleServices";
import { useSnackAlert } from "../../../common/use-snackAlert";
import { capitalize } from "../../../util/stringFormat";

import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";

import MediaDialog from "../../common/MediaDialog";
import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";
import { getStatusClass, useStatusStyles } from "../../common/statusStyles";

const useStyles = makeStyles(theme => ({
  actions: {
    padding: theme.spacing(5),
  },

  badge: {
    background: "rgba(125, 218, 252, 0.5)",
    borderRadius: 4,
    padding: "8px 12px",
    gap: 10,
    width: "auto",
    height: 32,
  },

  mediaType: {
    fontWeight: 700,
    fontSize: 14,
    lineHeight: "16px",
    color: theme.palette.primary.main,
  },

  title: {
    color: theme.palette.primary.darker,
    fontSize: "2.4rem",
    lineHeight: "3.2rem",
  },
}));

const ContentStatus = ({ status }) => {
  const classes = useStatusStyles();
  return (
    <span className={getStatusClass(status, classes)}>
      {capitalize(status ?? "")}
    </span>
  );
};

const BulkStatusConfirmDialog = ({
  open,
  targetStatus,
  contentItems,
  onConfirm,
  onClose,
}) => {
  const classes = useStyles();
  const { SnackAlert, showAlert } = useSnackAlert();

  const removeContentFromCollection = useRemoveContentFromCollection();
  const deleteModuleItems = useDeleteModuleItems();

  const allParents = uniqBy(
    prop("id"),
    contentItems.flatMap(item =>
      concat(item.parentModules ?? [], item.parentCollections ?? []),
    ),
  );

  const hasParents = !isEmpty(allParents);

  const removeParents = async () => {
    try {
      await Promise.all(
        contentItems.flatMap(item => {
          const parents = concat(
            item.parentModules ?? [],
            item.parentCollections ?? [],
          );
          return map(async parent => {
            if (equals(parent.mediaType, "module")) {
              await deleteModuleItems.mutateAsync({
                id: parent.id,
                ids: [item.id],
              });
            } else if (equals(parent.mediaType, "collection")) {
              await removeContentFromCollection.mutateAsync({
                collectionId: parent.id,
                contentIds: [item.id],
              });
            }
          }, parents);
        }),
      );
      onConfirm();
    } catch (err) {
      showAlert("error", [err.message]);
    }
  };

  const isLoading =
    removeContentFromCollection.isPending || deleteModuleItems.isPending;

  return (
    <Fragment>
      <SnackAlert />
      <MediaDialog
        open={open}
        titleClassName={classes.title}
        title={hasParents ? "Wait!" : "Confirm Status Change"}
        handleClose={onClose}
        content={
          isLoading ? (
            <PanelLoadingIndicator />
          ) : (
            <Grid container direction="column" spacing={4}>
              <Grid item xs={12}>
                <Typography>
                  {hasParents ? (
                    <>
                      Changing {contentItems.length} item(s) to{" "}
                      <ContentStatus status={targetStatus} /> will also remove
                      them from the following content groups:
                    </>
                  ) : (
                    <>
                      Are you sure you want to change {contentItems.length}{" "}
                      item(s) to <ContentStatus status={targetStatus} />?
                    </>
                  )}
                </Typography>
              </Grid>

              {hasParents && (
                <List>
                  {allParents.map(parent => (
                    <ListItem key={parent.id}>
                      <ListItemText>{parent.name}</ListItemText>
                      <div className={classes.badge}>
                        <Typography className={classes.mediaType}>
                          {toUpper(parent.mediaType)}
                        </Typography>
                      </div>
                    </ListItem>
                  ))}
                </List>
              )}
            </Grid>
          )
        }
        actionsClassName={classes.actions}
        actions={
          <Grid container alignItems="center">
            <Button
              variant="contained"
              color="primary"
              onClick={hasParents ? removeParents : onConfirm}
            >
              Confirm
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </Grid>
        }
      />
    </Fragment>
  );
};

export default BulkStatusConfirmDialog;
